import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { STATSIG_INIT_CONFIG, StatsigService } from '@statsig/angular-bindings';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  AppStorage,
  authInterceptor,
  CookieService,
  errorInterceptor,
  LanguageChangeService,
  StatsigUserService,
} from '@kuiper/ibusiness-core';
import { appRoutes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';

const DEFAULT_LANGUAGE = 'en';

export function preloadTranslation(transloco: TranslocoService) {
  return function () {
    const cookieService = inject(CookieService);
    const savedLang =
      cookieService.get(AppStorage.language) ?? DEFAULT_LANGUAGE;
    transloco.setActiveLang(savedLang);

    document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = savedLang;
    return transloco.load(savedLang);
  };
}

export function initLanguageChangeListener() {
  return () => {
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes),
    provideHttpClient(withInterceptors([errorInterceptor, authInterceptor])),
    LanguageChangeService,
    provideTransloco({
      config: {
        availableLangs: ['ar', 'en'],
        defaultLang: DEFAULT_LANGUAGE,
        // When a key is missing in Arabic, use English
        fallbackLang: DEFAULT_LANGUAGE,
        missingHandler: {
          useFallbackTranslation: true,
        },
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const initializerFn = preloadTranslation(inject(TranslocoService));
      return initializerFn();
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: () => initLanguageChangeListener(),
      deps: [LanguageChangeService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: STATSIG_INIT_CONFIG,
      useFactory: () => StatsigUserService.getInitialConfig(),
    },
    provideAppInitializer(() => {
      const statsig = inject(StatsigService);
      return firstValueFrom(
        statsig.isLoading$.pipe(filter(loading => !loading))
      );
    }),
  ],
};
